import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import TabContainerPanel from '../../../components/structure/Panels/TabContainerPanel';
import { TabPanel } from 'react-tabs';
import ScrollablePanelBody from '../../../components/structure/ScrollablePanelBody';
import Loader from '../../../components/general/Loader';
import PanelContainer from '../../../components/structure/Panels/PanelContainer';
import { color_shades_dark } from '../../../constants/colors';
import TemplateFormSectionModel from '../../../models/templating/api/TemplateFormSectionModel';
import TemplateConfigurationModel from '../../../models/templating/api/TemplateConfigurationModel';
import TemplateConfigurationContainer from '../../../state/containers/TemplateConfigurationContainer';
import PanelHeaderContainer from '../../../components/structure/Panels/PanelHeaderContainer';
import TemplateFieldSelector from './TemplateFieldSelector';
import TemplateSectionSelector from './TemplateSectionSelector';
import Button from '../../../components/general/Button';
import TemplateOverviewEditor from './TemplateOverviewEditor';
import TemplateSectionEditor from './TemplateSectionEditor';
import generateGuid from '../../../services/guids';
import _ from 'lodash';
import MediaContainer from '../../../state/containers/MediaContainer';
import MarkdownPreview from './MarkdownPreview';
import AppliedApplicationTemplateSectionForm from '../../formScenes/AppliedApplicationTemplateSectionForm';
import TemplateOverviewCard from '../../../components/templates/TemplateOverviewCard';
import SimpleFileImportDialog from '../../../components/general/SimpleFileImportDialog';
import ConfirmationDialog from '../../../components/structure/ConfirmationDialog';
import WarningConfirmation from '../../../components/general/WarningConfirmation';
import PageError from '../../../components/general/PageError';
import useCSVHandler from '../../../hooks/UseCSVHandler';
import useTemplateFormFieldsHandler from '../../../hooks/UseTemplateFormFieldsHandler';
import HorizontalSeparator from '../../../components/structure/HorizontalSeparator';
import SearchField from '../../../components/forms/SearchField';

const formFieldsIcon = require('../../../content/images/tab-icons/form-fields.svg');
const addIcon = require('../../../content/images/add-circle-dark.svg');
const fieldPreviewIcon = require('../../../content/images/tab-icons/field-preview.svg');
const fieldPreviewIconBlue = require('../../../content/images/tab-icons/field-preview-blue.svg');
const helpIconBlue = require('../../../content/images/tab-icons/help-info-blue.svg');
const helpIcon = require('../../../content/images/tab-icons/help-info.svg');
const exportIcon = require("../../../content/images/menu-item-icons/file-code-download.svg");
const importIcon = require("../../../content/images/menu-item-icons/file-code-upload.svg");

interface TemplateConfigurationContentProps {
    templateConfiguration: TemplateConfigurationModel
    templateConfigurationContainer: TemplateConfigurationContainer
    mediaContainer: MediaContainer
    isSaving: boolean
    onSaveClick: () => void
    organizationId: string
    history: any
    onChange: (templateConfiguration: TemplateConfigurationModel) => void
}

const TemplateDetailsEditor: React.FC<TemplateConfigurationContentProps> = ({
    templateConfiguration,
    templateConfigurationContainer,
    isSaving,
    onSaveClick,
    onChange
}) => {
    const [isFinishSelected, setIsFinishSelected] = useState(false);
    const [isStartSelected, setIsStartSelected] = useState(true);
    const [currentSection, setCurrentSection] = useState<TemplateFormSectionModel>(null);
    const [highlightedFieldId, setHighlightedFieldId] = useState<string | null>(null);
    const [fieldSearch, setFieldSearch] = useState('');
    const [isImportFileDialogModalOpen, setIsImportFileDialogModalOpen] = useState(false);
    const [pageErrors, setPageErrors] = useState<string[]>([]);
    const [isConfirmImportModalOpen, setIsConfirmImportModalOpen] = useState(false);
    const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);

    const { isLoadingImport, setIsLoadingImport } = useCSVHandler();

    // New template-specific hook (only using export functionality)
    const {
        exportTemplateFormFields,
        importTemplateFormFields,
        importedData: importedSections,
        mergeTemplateSections,
        errors: templateErrors
    } = useTemplateFormFieldsHandler();

    useEffect(() => {
        if (importedSections && importedSections.length > 0) {
            const updatedTemplate = mergeTemplateSections(importedSections, templateConfiguration);
            onChange(updatedTemplate);
        }
    }, [importedSections]);

    useEffect(() => {
        if (templateErrors && templateErrors.length > 0) {
            setPageErrors(prev => [...prev, ...templateErrors]);
        }
    }, [templateErrors]);

    // Re-sync currentSection reference whenever templateConfiguration changes (e.g. after save
    // the server returns a new object with new references — re-select by id so the sidebar
    // highlight is restored).
    useEffect(() => {
        if (!currentSection) return;
        const sections = templateConfiguration?.templateForm?.templateFormSections;
        if (!sections) return;
        const matched = sections.find(s => s.id === currentSection.id);
        if (matched && matched !== currentSection) {
            setCurrentSection(matched);
        }
    }, [templateConfiguration]);

    // After navigating to a field, scroll it into view once it has rendered.
    useEffect(() => {
        if (!highlightedFieldId) return;
        const element = document.getElementById(highlightedFieldId);
        if (element) {
            const scrollable = element.closest('.scrollable-panel');
            if (scrollable) {
                const panelBody = scrollable.children[0] as HTMLElement;
                if (panelBody) {
                    panelBody.scrollTop = (element as HTMLElement).offsetTop - 30;
                }
            } else {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            setHighlightedFieldId(null);
        }
    }, [highlightedFieldId, currentSection]);

    const handleNextPage = () => {
        const sections = templateConfiguration?.templateForm?.templateFormSections;
        if (isStartSelected && sections && sections.length > 0) {
            setIsStartSelected(false);
            setCurrentSection(sections ? sections[0] : null);
        } else if (sections && sections.length > 0 && currentSection != sections[sections.length - 1]) {
            setIsStartSelected(false);
            setCurrentSection(sections[sections.indexOf(currentSection) + 1]);
        }
    };

    const handlePrevPage = () => {
        const sections = templateConfiguration?.templateForm?.templateFormSections;
        if (!isStartSelected && currentSection != sections[0]) {
            setIsStartSelected(false);
            setCurrentSection(sections[sections.indexOf(currentSection) - 1]);
        }
        else if (currentSection == sections[0]) {
            setIsStartSelected(true);
            setCurrentSection(null);
        }
    };

    const handleStartSelected = () => {
        setIsFinishSelected(false);
        setIsStartSelected(true);
        setCurrentSection(null);
    };

    const handleSectionSelected = (section: TemplateFormSectionModel) => {
        setCurrentSection(section);
        setIsFinishSelected(false);
        setIsStartSelected(false);
    };

    const handleAddSection = () => {
        const templateConfig = templateConfiguration;
        if (!templateConfig.templateForm?.templateFormSections)
            templateConfig.templateForm.templateFormSections = [];

        templateConfig.templateForm.templateFormSections.push({
            id: generateGuid(),
            title: 'New Page',
            instructionsMarkdown: '',
            templateFormFields: [],
        });
        templateConfig.templateForm?.templateFormSections?.forEach((s, i) => {
            s.priority = i
        });
        handleTemplateUpdate(templateConfig);
        handleSectionSelected(templateConfig.templateForm.templateFormSections[templateConfig.templateForm.templateFormSections.length - 1]);
    };

    const handleTemplateUpdate = (template: TemplateConfigurationModel) => {
        onChange(template);
    };

    const handleAddField = (fieldType: string) => {
        const section = templateConfiguration.templateForm.templateFormSections.find(s => s == currentSection);

        if (section.templateFormFields == null)
            section.templateFormFields = [];

        section.templateFormFields.push({
            id: generateGuid(),
            associatedVariable: '',
            title: '',
            tip: '',
            placeholder: '',
            options: [],
            isRequired: false,
            templateFormSectionId: section.id,
            fieldType
        });

        section.templateFormFields.forEach((f, i) => {
            f.priority = i;
        });
        handleTemplateUpdate(templateConfiguration);
    };

    const handleSortSections = (sections: TemplateFormSectionModel[]) => {
        sections.forEach((s, i) => {
            s.priority = i;
        });

        const templateConfig = templateConfiguration;
        templateConfig.templateForm.templateFormSections = sections;

        handleTemplateUpdate(templateConfig);
    };

    const handleDeleteSection = (section: TemplateFormSectionModel) => {
        const templateConfig = templateConfiguration;
        _.remove(templateConfig.templateForm.templateFormSections, s => s.id == section.id);

        setIsStartSelected(true);
        setCurrentSection(null);
        handleTemplateUpdate(templateConfig);
    };

    const handleNavigateToField = (targetFieldId: string) => {
        const sections = templateConfiguration?.templateForm?.templateFormSections;
        if (!sections) return;
        const targetSection = sections.find(s =>
            s.templateFormFields?.some(f => f.id === targetFieldId)
        );
        if (targetSection) {
            handleSectionSelected(targetSection);
            setHighlightedFieldId(targetFieldId);
        }
    };

    const toggleImportFileDialogModal = () => {
        setIsImportFileDialogModalOpen(!isImportFileDialogModalOpen);
        if (isImportFileDialogModalOpen) {
            setPageErrors([]);
        }
    };

    const toggleConfirmImportModal = () => {
        setIsConfirmImportModalOpen(!isConfirmImportModalOpen);
        // Clear the selected file if the modal is being closed
        if (isConfirmImportModalOpen) {
            setSelectedImportFile(null);
        }
    };

    const importFormFields = (file: File) => {
        setPageErrors([]);
        // Store the file for later processing
        setSelectedImportFile(file);
        toggleImportFileDialogModal();
        toggleConfirmImportModal();
    };

    const useImportedTemplateFormFields = async () => {
        if (!selectedImportFile) return;

        setIsLoadingImport(true);

        try {
            // Process the CSV file and import the data
            await importTemplateFormFields(selectedImportFile);
            // The useEffect will handle updating the template when importedSections changes
        } catch (error) {
            console.error('Error importing template form fields:', error);
        } finally {
            setIsLoadingImport(false);
            setSelectedImportFile(null);
            toggleConfirmImportModal();
        }
    };

    const renderMenu = () => {
        return (
            <div className={"fixed-wrapper"}>
                <PanelHeaderContainer className="template-section-menu-header">
                    <h4>Sections</h4>
                    <Button type="button" themes={['end', 'icon']} icon={addIcon} onClick={handleAddSection} />
                </PanelHeaderContainer>
                <ScrollablePanelBody className={panelBodyStyle} autoHeight={true} autoHeightMax={"calc(100vh - 136px)"}>
                    <TemplateSectionSelector
                        sections={templateConfiguration?.templateForm?.templateFormSections ?? []}
                        activeSection={currentSection}
                        isStartSelected={isStartSelected}
                        onStartSelect={handleStartSelected}
                        onChange={handleSectionSelected}
                        onSort={handleSortSections}
                        onFieldSelect={handleNavigateToField}
                    />
                    <HorizontalSeparator />
                    <FormPopulationButtonWrapper>
                        <FormPopulationButton
                            disabled={false}
                            loading={false}
                            type="button"
                            themes={['secondary-small', 'end-tight']}
                            rightIcon={exportIcon}
                            onClick={() => {
                                exportTemplateFormFields(templateConfiguration, templateConfiguration?.name);
                            }}
                            text="Export Form"
                        />
                        <FormPopulationButton
                            type="button"
                            themes={['secondary-small', 'end-tight']}
                            rightIcon={importIcon}
                            onClick={() => {
                                toggleImportFileDialogModal();
                            }}
                            text="Import Form"
                        />
                    </FormPopulationButtonWrapper>
                </ScrollablePanelBody>
            </div>);
    };

    const getTabs = () => {
        // if we are on the start page, then don't add the fields tab
        const tabs = [{
            title: "Step Preview",
            icon: fieldPreviewIcon,
            selectedIcon: fieldPreviewIconBlue
        }, {
            title: 'Search Fields',
            icon: helpIcon,
            selectedIcon: helpIconBlue
        }];
        if (!isStartSelected && !isFinishSelected) {
            tabs.unshift({
                title: 'Form Fields',
                icon: formFieldsIcon,
                selectedIcon: formFieldsIcon
            })
        }
        return tabs;
    };

    const { isLoading } = templateConfigurationContainer.state;
    if (isLoading)
        return <Loader />

    return (
        <PanelContainer>
            <div className={`${menuStyle} expanded`}>
                {renderMenu()}
            </div>
            <div className={middlePanelContainer}>
                <PanelHeaderContainer className="panel-header">
                    <Button type="button" themes={['primary-small', 'start']} text="Save" className="save-button" disabled={isSaving} loading={isSaving} onClick={() => onSaveClick()} />
                    <Button type="button" themes={['secondary-small', 'end']} text="Prev Step" className="prev-button" disabled={isStartSelected} onClick={handlePrevPage} />
                    <Button type="button" themes={['secondary-small', 'end']} text="Next Step" className="next-button" onClick={handleNextPage} />
                </PanelHeaderContainer>
                <ScrollablePanelBody>
                    {isStartSelected
                        ? <TemplateOverviewEditor onChange={onChange} templateConfiguration={templateConfiguration} />
                        : <TemplateSectionEditor onChange={handleTemplateUpdate} section={currentSection} templateConfiguration={templateConfiguration} onDelete={handleDeleteSection} onNavigateToField={handleNavigateToField} highlightedFieldId={highlightedFieldId} />}
                </ScrollablePanelBody>
            </div>
            <TabContainerPanel tabs={getTabs()}>
                {!isStartSelected && !isFinishSelected && <TabPanel>
                    <ScrollablePanelBody>
                        <TemplateFieldSelector templateConfiguration={templateConfiguration} section={currentSection} onFieldSelected={handleAddField} />
                    </ScrollablePanelBody>
                </TabPanel>}
                <TabPanel>
                    <ScrollablePanelBody>
                        {isStartSelected
                            ? <TemplateOverviewCard templateConfiguration={templateConfiguration} />
                            : <AppliedApplicationTemplateSectionForm fieldsOnly values={[]} onFieldValueChange={() => { }} section={currentSection} applicationId={null} preview={true} />
                        }
                    </ScrollablePanelBody>
                </TabPanel>
                <TabPanel>
                    {/* Search Fields tab — replaces Helpful Info */}
                    <ScrollablePanelBody className={mainBodyStyle}>
                        <SearchField
                            placeholder="Search fields..."
                            value={fieldSearch}
                            onChange={e => setFieldSearch(e.target.value)}
                            handleClear={() => setFieldSearch('')}
                            boxType="square"
                        />
                        {fieldSearch.trim().length > 0 && (() => {
                            const query = fieldSearch.toLowerCase();
                            const matches = (currentSection?.templateFormFields ?? []).filter(f =>
                                f.title?.toLowerCase().includes(query) ||
                                f.associatedVariable?.toLowerCase().includes(query)
                            );
                            if (matches.length === 0) {
                                return <p className={noResultsStyle}>No fields match "{fieldSearch}"</p>;
                            }
                            return (
                                <ul className={searchResultsStyle}>
                                    {matches.map(f => (
                                        <li key={f.id} onClick={() => handleNavigateToField(f.id)}>
                                            <p className="field-title">{f.title || <em>Untitled</em>}</p>
                                            {f.associatedVariable && <p className="field-variable">{f.associatedVariable}</p>}
                                        </li>
                                    ))}
                                </ul>
                            );
                        })()}
                    </ScrollablePanelBody>
                    {/* Helpful Info content — commented out in case we want to bring it back
                    <ScrollablePanelBody className={mainBodyStyle}>
                        <MarkdownPreview instructionsMarkdown={isStartSelected ? (templateConfiguration?.templateForm?.instructionsMarkdown ?? '') : currentSection?.instructionsMarkdown ?? ''} />
                    </ScrollablePanelBody>
                    */}
                </TabPanel>
            </TabContainerPanel>
            {isConfirmImportModalOpen &&
                <ConfirmationDialog
                    title="Confirm Form Import"
                    deleteText="Yes, Import"
                    isLoading={isLoadingImport}
                    onClose={toggleConfirmImportModal}
                    onConfirm={useImportedTemplateFormFields}
                    warning={true}>
                    <WarningConfirmation
                        text="Importing this form will update fields with matching associated variables, add new fields from the CSV, and preserve any existing fields not found in the CSV. Field IDs and associated variables will never change."
                        useWarningColor={true}
                    />
                </ConfirmationDialog>
            }
            {isImportFileDialogModalOpen &&
                <SimpleFileImportDialog
                    onClose={toggleImportFileDialogModal}
                    completeImport={importFormFields}
                    title={"Select a .csv File to Import"}
                    acceptFileTypes={".csv"}
                    acceptFileTypesDisplayText={"Supports .csv"}
                    isLoading={false}
                    errors={templateErrors}
                />
            }
            <PageError
                errors={pageErrors}
                onClear={() => setPageErrors([])}
            />
        </PanelContainer>
    );
};

export default TemplateDetailsEditor;

const middlePanelContainer = css`
    flex: 1;
    border-right: 1px solid ${color_shades_dark};
    .overview-empty-label {
        margin: 32px;
    }
    .panel-header {
        button {
            margin: 0;
        }
        .save-button {
            margin-left: 32px;
        }
        .next-button {
            margin-right: 32px;
        }
        .prev-button {
            margin-right: 16px;
            margin-left: auto;
        }
    }
`

const panelBodyStyle = css`
    padding-top: 0;
    min-height: calc(100vh - 160px);
    height: auto;
    overflow-y: auto;
`;

const mainBodyStyle = css`
    padding: 24px 32px 64px 32px;
`

const noResultsStyle = css`
    font-size: 14px;
    font-style: italic;
    color: #999;
    margin-top: 8px;
`

const searchResultsStyle = css`
    list-style: none;
    padding: 0;
    margin: 8px 0 0 0;
    li {
        padding: 10px 12px;
        border-radius: 4px;
        cursor: pointer;
        border-bottom: 1px solid #eee;
        &:hover {
            background: #f5f8ff;
        }
        .field-title {
            font-size: 14px;
            font-weight: 600;
            margin: 0;
        }
        .field-variable {
            font-size: 12px;
            color: #999;
            margin: 2px 0 0 0;
        }
    }
`

const FormPopulationButton = styled(Button)`
    margin: 0 0 16px 8px;
    font-size: 14px;
    font-weight: 400;
    .right-img {
        margin-left: 8px;
        width: 16px;
    }
`;

const FormPopulationButtonWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    margin-top: 20px;
    margin-left: 16px;
`;

const menuStyle = css`
z-index: 2;
bottom: 0;
position: relative;
.template-section-menu-header {    
    h4 {
        font-family: Muli;
        font-style: normal;
        font-weight: normal;
        font-size: 16px;
        line-height: 24px;
        margin: 0 32px;
    }
    button {
        margin-right: 16px;
        margin-left: auto;
    }
}
&.collapsed {
    width: 80px;
    .fixed-wrapper {
        width: 80px;
    }
}
&.expanded {
    width: 288px; 
    .fixed-wrapper {
        width: 288px;
    }
}

.fixed-wrapper {
    position: fixed;
    bottom: 0;
    height: calc(100vh - 80px);
    background: white;
    border-right: 1px solid ${color_shades_dark};
    box-sizing: border-box;
}

`
